import * as React from "react";

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { getDataTablePageNumbers } from "@/lib/data-table-pagination";
import { cn } from "@/lib/utils";

export interface DataTablePaginationControlsProps {
  /** Zero-based current page index. */
  pageIndex: number;
  /** Total number of pages (at least 1 when visible). */
  pageCount: number;
  onPageIndexChange: (pageIndex: number) => void;
  /** Override when page bounds are estimated (e.g. server-side paging). */
  canPreviousPage?: boolean;
  canNextPage?: boolean;
  className?: string;
}

/** Shadcn Previous / numbered pages / Next — shared by DataTable and server-paged lists. */
export function DataTablePaginationControls({
  pageIndex,
  pageCount,
  onPageIndexChange,
  canPreviousPage: canPreviousPageProp,
  canNextPage: canNextPageProp,
  className,
}: DataTablePaginationControlsProps) {
  const safePageCount = Math.max(pageCount, 0);
  const safePageIndex = Math.min(
    Math.max(pageIndex, 0),
    Math.max(safePageCount - 1, 0),
  );

  const canPreviousPage =
    canPreviousPageProp ?? safePageIndex > 0;
  const canNextPage =
    canNextPageProp ?? safePageIndex < safePageCount - 1;

  const pageNumbers = React.useMemo(
    () => getDataTablePageNumbers(safePageIndex, safePageCount),
    [safePageIndex, safePageCount],
  );

  if (safePageCount <= 0) {
    return null;
  }

  return (
    <Pagination className={cn("mx-0 w-auto justify-end", className)}>
      <PaginationContent className="gap-3">
        <PaginationItem className="shrink-0">
          <PaginationPrevious
            disabled={!canPreviousPage}
            aria-disabled={!canPreviousPage}
            className={cn(
              !canPreviousPage && "pointer-events-none opacity-50",
            )}
            onClick={() => {
              if (canPreviousPage) {
                onPageIndexChange(safePageIndex - 1);
              }
            }}
          />
        </PaginationItem>
        {pageNumbers.length > 0 ? (
          <PaginationItem className="flex shrink-0 items-center">
            <ul
              data-slot="pagination-pages"
              aria-label="Pages"
              className="m-0 flex list-none flex-row items-center gap-1 p-0"
            >
              {pageNumbers.map((page, index) =>
                page === "ellipsis" ? (
                  <li key={`ellipsis-${index}-${safePageIndex}`}>
                    <PaginationEllipsis />
                  </li>
                ) : (
                  <li key={`page-${page}`}>
                    <PaginationLink
                      isActive={safePageIndex === page}
                      onClick={() => {
                        onPageIndexChange(page);
                      }}
                    >
                      {page + 1}
                    </PaginationLink>
                  </li>
                ),
              )}
            </ul>
          </PaginationItem>
        ) : null}
        <PaginationItem className="shrink-0">
          <PaginationNext
            disabled={!canNextPage}
            aria-disabled={!canNextPage}
            className={cn(!canNextPage && "pointer-events-none opacity-50")}
            onClick={() => {
              if (canNextPage) {
                onPageIndexChange(safePageIndex + 1);
              }
            }}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
